import * as React from 'react';
import { useRecoilValue } from 'recoil';

import * as analytics from '@gitbook/app-analytics/client';
import { UserTheme } from '@gitbook/app-database';
import { ConfigurationTheme } from '@gitbook/react-uikit';
import { ThemeProvider } from '@gitbook/spine-react';
import { usePrevious } from '@gitbook/util-react-hooks';

import { useCurrentUser } from '../auth';
import { userThemeAtom } from '../locals';

/*
 * Provide all user data as a context.
 *
 * We try to do many request in parallel as to only suspend once.
 */
export function UserDataProvider(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;
    const user = useCurrentUser();
    const defaultColorScheme = usePrefersColorScheme();
    const userTheme = useRecoilValue(userThemeAtom);

    // Identify the user everytime it changes
    const prevUserId = usePrevious(user?.uid);
    React.useEffect(() => {
        if (user?.uid !== prevUserId) {
            analytics.identify();
        }
    }, [user?.uid, prevUserId]);

    const theme = userTheme === UserTheme.Default ? defaultColorScheme : userTheme;

    return (
        <ConfigurationTheme theme={{ color: theme || undefined }}>
            <ThemeProvider value={theme || 'light'}>{children}</ThemeProvider>
        </ConfigurationTheme>
    );
}

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * The color scheme preferred by the operating system, kept in sync with changes.
 */
function usePrefersColorScheme(): 'light' | 'dark' {
    return React.useSyncExternalStore(
        (onChange) => {
            const mql = window.matchMedia(COLOR_SCHEME_QUERY);
            if (!mql) {
                return () => {};
            }
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        },
        () => (window.matchMedia(COLOR_SCHEME_QUERY).matches ? 'dark' : 'light'),
        () => 'light'
    );
}
